import React, { useEffect, useState } from 'react';
import { useNavigate, Link, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2, Plus, X } from 'lucide-react';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { newslettersApi, signupFormsApi, SignupFormRecord, extractLookupId, NewsletterTagRow, NewsletterCategoryRuleRow, NewsletterMailingListRow } from '../services/creatorApi';
import { crmMailingListsApi, crmTagsApi, crmCategoriesApi, CrmMailingList, CrmTag, CrmCategory } from '../services/crmApi';
import { toast } from 'sonner';
function extractFormFields(html: string): string[] {
  const fields: string[] = [];
  const regex = /(?:name|id)=["']([^"']+)["']/gi;
  let match;
  while ((match = regex.exec(html)) !== null) {
    const name = match[1];
    if (name && !fields.includes(name) && !['submit', 'button'].includes(name.toLowerCase())) {
      fields.push(name);
    }
  }
  return fields;
}
export function NewsletterEditor() {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [forms, setForms] = useState<SignupFormRecord[]>([]);
  const [mailingLists, setMailingLists] = useState<CrmMailingList[]>([]);
  const [tags, setTags] = useState<CrmTag[]>([]);
  const [categories, setCategories] = useState<CrmCategory[]>([]);
  const [selectedFormHtml, setSelectedFormHtml] = useState('');
  // Form Data
  const [formData, setFormData] = useState({
    name: '',
    newsletterId: '',
    formId: '',
    mailingListIds: [] as string[],
    tagIds: [] as string[],
    categoryRules: [] as NewsletterCategoryRuleRow[]
  });
  // Inline Create State
  const [showCreateList, setShowCreateList] = useState(false);
  const [newListName, setNewListName] = useState('');
  const [creatingList, setCreatingList] = useState(false);
  const [showCreateTag, setShowCreateTag] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  // Fetch Data
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [formsResult, listsResult, tagsResult, catsResult] = await Promise.all([signupFormsApi.list(), crmMailingListsApi.list(), crmTagsApi.list(), crmCategoriesApi.list()]);
        const formsData = formsResult;
        const listsData = listsResult;
        const tagsData = tagsResult;
        const catsData = catsResult;
        // Filter forms by team so users only see their own team's forms
        const teamFilteredForms = formsData.filter(f => f.Team === currentWorkspace.name || f.Team === (currentSubRegion || currentWorkspace.name.replace(/\s*Team\s*$/i, '')));
        setForms(teamFilteredForms);
        setMailingLists(listsData);
        setTags(tagsData);
        setCategories(catsData);
        if (isEditMode && id) {
          const newsletter = await newslettersApi.get(id);
          if (newsletter) {
            const tagIds = (newsletter.Newsletter_Tags || []).map((t: NewsletterTagRow) => t.Tag_ID);
            const mailingListIds = (newsletter.Mailing_Lists || []).map((l: NewsletterMailingListRow) => l.Mailing_List_ID);
            // Keep the full category rules (with IDs)
            const categoryRules = newsletter.Newsletter_Category_Rules || [];
            const selectedFormId = extractLookupId(newsletter.Sign_Up_Form);
            setFormData({
              name: newsletter.Newsletter,
              newsletterId: newsletter.Newsletter_ID || '',
              formId: selectedFormId,
              mailingListIds,
              tagIds,
              categoryRules
            });
          }
        }
      } catch (err) {
        console.error('Failed to load data:', err);
        toast.error('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [id, isEditMode]);
  // Fetch Form HTML when formId changes to extract fields
  useEffect(() => {
    if (!formData.formId) {
      setSelectedFormHtml('');
      return;
    }
    signupFormsApi.get(formData.formId).then(record => {
      if (record) {
        setSelectedFormHtml(record.HTML || '');
      }
    }).catch(err => console.error('Failed to fetch form details', err));
  }, [formData.formId]);
  const formFields = selectedFormHtml ? extractFormFields(selectedFormHtml) : [];
  // Handlers
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Newsletter name is required');
      return;
    }
    setSaving(true);
    try {
      // Build subform rows for Tags — use CRM tag record ID directly as subform ID
      const tagRows: NewsletterTagRow[] = formData.tagIds.map(tagId => {
        const tag = tags.find(t => t.id === tagId);
        if (!tag) return null;
        return {
          ID: tag.id,
          Tag_ID: tag.id,
          Tag_Name: tag.Name
        };
      }).filter(Boolean) as NewsletterTagRow[];
      // Build subform rows for Mailing Lists — use CRM mailing list record ID directly as subform ID
      const mailingListRows: NewsletterMailingListRow[] = formData.mailingListIds.map(listId => {
        const list = mailingLists.find(l => l.id === listId);
        if (!list) return null;
        return {
          ID: list.id,
          Mailing_List_ID: list.id,
          Mailing_List_Name: list.Name
        };
      }).filter(Boolean) as NewsletterMailingListRow[];
      const newsletterId = formData.newsletterId || newslettersApi.generateId();
      const payload = {
        Newsletter: formData.name,
        Newsletter_ID: newsletterId,
        Sign_Up_Form: formData.formId,
        Team: currentWorkspace.name,
        Region: currentSubRegion || '',
        Mailing_Lists: mailingListRows,
        Newsletter_Tags: tagRows,
        Newsletter_Category_Rules: formData.categoryRules
      };
      if (isEditMode && id) {
        await newslettersApi.update(id, payload);
        toast.success('Newsletter updated');
      } else {
        await newslettersApi.create(payload);
        toast.success('Newsletter created');
      }
      navigate('/newsletters');
    } catch (err) {
      console.error('Failed to save newsletter:', err);
      toast.error('Failed to save newsletter');
    } finally {
      setSaving(false);
    }
  };
  // Inline Create Handlers
  const handleCreateList = async () => {
    if (!newListName.trim()) return;
    setCreatingList(true);
    try {
      await crmMailingListsApi.create({
        Name: newListName,
        Team: currentWorkspace.name,
        Region: currentSubRegion || undefined,
        Status: 'open'
      });
      const updatedLists = await crmMailingListsApi.list();
      setMailingLists(updatedLists);
      const newList = updatedLists.find(l => l.Name === newListName);
      if (newList) {
        setFormData(prev => ({
          ...prev,
          mailingListIds: [...prev.mailingListIds, newList.id]
        }));
      }
      setShowCreateList(false);
      setNewListName('');
      toast.success('Mailing list created');
    } catch (err) {
      toast.error('Failed to create mailing list');
    } finally {
      setCreatingList(false);
    }
  };
  const handleCreateTag = async () => {
    if (!newTagName.trim()) return;
    setCreatingTag(true);
    try {
      await crmTagsApi.create({
        Name: newTagName,
        Team: currentWorkspace.name
      });
      const updatedTags = await crmTagsApi.list();
      setTags(updatedTags);
      const newTag = updatedTags.find(t => t.Name === newTagName);
      if (newTag) {
        setFormData(prev => ({
          ...prev,
          tagIds: [...prev.tagIds, newTag.id]
        }));
      }
      setShowCreateTag(false);
      setNewTagName('');
      toast.success('Tag created');
    } catch (err) {
      toast.error('Failed to create tag');
    } finally {
      setCreatingTag(false);
    }
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-5326">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5327" />
      </div>;
  }
  return <div className="space-y-6 max-w-4xl pb-20" data-id="element-5328">
      <div className="flex items-center gap-4" data-id="element-5329">
        <Link to="/newsletters" className="p-2 hover:bg-gray-100 rounded-full text-gray-500" data-id="element-5330">
          <ArrowLeft className="h-5 w-5" data-id="element-5331" />
        </Link>
        <div data-id="element-5332">
          <h1 className="text-2xl font-semibold text-gray-900" data-id="element-5333">
            {isEditMode ? 'Edit Newsletter' : 'Create Newsletter'}
          </h1>
          <p className="text-sm text-gray-500" data-id="element-5334">
            Configure audience lists and CRM integration.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8" data-id="element-5335">
        {/* Basic Info */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-5336">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5337">
            Basic Information
          </h2>
          <div className="grid grid-cols-1 gap-6" data-id="element-5338">
            <div data-id="element-5339">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5340">
                Newsletter Name
              </label>
              <input type="text" required className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.name} onChange={e => setFormData({
              ...formData,
              name: e.target.value
            })} placeholder="e.g., Weekly Updates" data-id="element-5341" />
            </div>

            {isEditMode && formData.newsletterId && <div data-id="element-5342">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5343">
                  Newsletter ID
                </label>
                <input type="text" className="block w-full rounded-md border-gray-300 bg-gray-50 shadow-sm sm:text-sm text-gray-500 cursor-not-allowed font-mono" value={formData.newsletterId} disabled data-id="element-5344" />
                <p className="mt-1 text-xs text-gray-400" data-id="element-5345">
                  Unique identifier used to link form submissions to this
                  newsletter.
                </p>
              </div>}

            <div data-id="element-5346">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5347">
                Sign Up Form
              </label>
              <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={formData.formId} onChange={e => setFormData({
              ...formData,
              formId: e.target.value
            })} data-id="element-5348">
                <option value="" data-id="element-5349">Select a form...</option>
                {forms.map(f => <option key={f.ID} value={f.ID} data-id="element-5350">
                    {f.Form_Name}
                  </option>)}
              </select>
            </div>

            <div className="bg-gray-50 rounded-lg p-4 space-y-2" data-id="element-5351">
              <h3 className="text-sm font-medium text-gray-900" data-id="element-5352">Scope</h3>
              <div className="text-sm text-gray-600 grid grid-cols-2 gap-4" data-id="element-5353">
                <div data-id="element-5354">
                  <strong data-id="element-5355">Team:</strong> {currentWorkspace.name}
                </div>
                <div data-id="element-5356">
                  <strong data-id="element-5357">Region:</strong> {currentSubRegion || 'All Regions'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CRM Integration */}
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 space-y-6" data-id="element-5358">
          <h2 className="text-lg font-medium text-gray-900" data-id="element-5359">CRM Integration</h2>

          {/* Mailing Lists (Multi-select) */}
          <div data-id="element-5360">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5361">
              Assign to Mailing Lists
            </label>
            <div className="space-y-2" data-id="element-5362">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-5363">
                {formData.mailingListIds.map(listId => {
                const list = mailingLists.find(l => l.id === listId);
                return list ? <span key={listId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200" data-id="element-5364">
                      {list.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    mailingListIds: prev.mailingListIds.filter(id => id !== listId)
                  }))} className="hover:text-blue-900" data-id="element-5365">
                        <X className="h-3 w-3" data-id="element-5366" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-5367">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.mailingListIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    mailingListIds: [...prev.mailingListIds, e.target.value]
                  }));
                }
              }} data-id="element-5368">
                  <option value="" data-id="element-5369">Add a mailing list...</option>
                  {mailingLists.filter(l => !formData.mailingListIds.includes(l.id)).map(l => <option key={l.id} value={l.id} data-id="element-5370">
                        {l.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateList(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new mailing list" data-id="element-5371">
                  <Plus className="h-4 w-4" data-id="element-5372" />
                </button>
              </div>
              {showCreateList && <div className="mt-2 flex gap-2 items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-5373">
                  <input type="text" placeholder="New mailing list name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newListName} onChange={e => setNewListName(e.target.value)} data-id="element-5374" />
                  <button type="button" onClick={handleCreateList} disabled={creatingList || !newListName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-5375">
                    {creatingList ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5376" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateList(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-5377">
                    <X className="h-4 w-4" data-id="element-5378" />
                  </button>
                </div>}
            </div>
          </div>

          {/* Tags (Multi-select) */}
          <div data-id="element-5379">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-5380">
              Assign Tags
            </label>
            <div className="space-y-2" data-id="element-5381">
              <div className="flex flex-wrap gap-2 mb-2" data-id="element-5382">
                {formData.tagIds.map(tagId => {
                const tag = tags.find(t => t.id === tagId);
                return tag ? <span key={tagId} className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-sm bg-blue-50 text-blue-700 border border-blue-200" data-id="element-5383">
                      {tag.Name}
                      <button type="button" onClick={() => setFormData(prev => ({
                    ...prev,
                    tagIds: prev.tagIds.filter(id => id !== tagId)
                  }))} className="hover:text-blue-900" data-id="element-5384">
                        <X className="h-3 w-3" data-id="element-5385" />
                      </button>
                    </span> : null;
              })}
              </div>
              <div className="flex gap-2" data-id="element-5386">
                <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value="" onChange={e => {
                if (e.target.value && !formData.tagIds.includes(e.target.value)) {
                  setFormData(prev => ({
                    ...prev,
                    tagIds: [...prev.tagIds, e.target.value]
                  }));
                }
              }} data-id="element-5387">
                  <option value="" data-id="element-5388">Add a tag...</option>
                  {tags.filter(t => !formData.tagIds.includes(t.id)).map(t => <option key={t.id} value={t.id} data-id="element-5389">
                        {t.Name}
                      </option>)}
                </select>
                <button type="button" onClick={() => setShowCreateTag(true)} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50" title="Create new tag" data-id="element-5390">
                  <Plus className="h-4 w-4" data-id="element-5391" />
                </button>
              </div>
              {showCreateTag && <div className="mt-2 flex gap-2 items-center bg-gray-50 p-3 rounded-md border border-gray-200" data-id="element-5392">
                  <input type="text" placeholder="New tag name" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm" value={newTagName} onChange={e => setNewTagName(e.target.value)} data-id="element-5393" />
                  <button type="button" onClick={handleCreateTag} disabled={creatingTag || !newTagName.trim()} className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-id="element-5394">
                    {creatingTag ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-5395" /> : 'Create'}
                  </button>
                  <button type="button" onClick={() => setShowCreateTag(false)} className="p-2 text-gray-400 hover:text-gray-600" data-id="element-5396">
                    <X className="h-4 w-4" data-id="element-5397" />
                  </button>
                </div>}
            </div>
          </div>

          {/* Categories (Conditional Rules) */}
          <div className="pt-4 border-t border-gray-200" data-id="element-5398">
            <div className="flex items-center justify-between mb-2" data-id="element-5399">
              <label className="block text-sm font-medium text-gray-700" data-id="element-5400">
                Conditional Category Rules
              </label>
              <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                categoryRules: [...prev.categoryRules, {
                  Form_Field: formFields[0] || '',
                  Condition: 'equals',
                  Value: '',
                  Category_ID: '',
                  Category_Name: ''
                }]
              }));
            }} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-id="element-5401">
                + Add Rule
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-4" data-id="element-5402">
              Assign CRM categories to subscribers based on their form field
              values.
            </p>

            {formData.categoryRules.length > 0 ? <div className="space-y-3" data-id="element-5403">
                {formData.categoryRules.map((rule, index) => <div key={index} className="p-3 bg-gray-50 border border-gray-200 rounded-md space-y-3 relative" data-id="element-5404">
                    <button type="button" onClick={() => {
                setFormData(prev => ({
                  ...prev,
                  categoryRules: prev.categoryRules.filter((_, i) => i !== index)
                }));
              }} className="absolute top-2 right-2 text-gray-400 hover:text-red-500" data-id="element-5405">
                      <X className="h-4 w-4" data-id="element-5406" />
                    </button>
                    <div className="text-xs font-medium text-gray-500 uppercase" data-id="element-5407">
                      Rule {index + 1}
                    </div>

                    <div className="grid grid-cols-3 gap-2" data-id="element-5408">
                      <div className="col-span-1" data-id="element-5409">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5410">
                          Field
                        </label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Form_Field} onChange={e => {
                    const newRules = [...formData.categoryRules];
                    newRules[index].Form_Field = e.target.value;
                    setFormData({
                      ...formData,
                      categoryRules: newRules
                    });
                  }} data-id="element-5411">
                          <option value="" data-id="element-5412">Select field...</option>
                          {formFields.map(f => <option key={f} value={f} data-id="element-5413">
                              {f}
                            </option>)}
                        </select>
                      </div>
                      <div className="col-span-1" data-id="element-5414">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5415">
                          Condition
                        </label>
                        <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Condition} onChange={e => {
                    const newRules = [...formData.categoryRules];
                    newRules[index].Condition = e.target.value;
                    setFormData({
                      ...formData,
                      categoryRules: newRules
                    });
                  }} data-id="element-5416">
                          <option value="equals" data-id="element-5417">Equals</option>
                          <option value="not_equals" data-id="element-5418">Not Equals</option>
                          <option value="contains" data-id="element-5419">Contains</option>
                        </select>
                      </div>
                      <div className="col-span-1" data-id="element-5420">
                        <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5421">
                          Value
                        </label>
                        <input type="text" className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Value} onChange={e => {
                    const newRules = [...formData.categoryRules];
                    newRules[index].Value = e.target.value;
                    setFormData({
                      ...formData,
                      categoryRules: newRules
                    });
                  }} placeholder="e.g. Male" data-id="element-5422" />
                      </div>
                    </div>

                    <div data-id="element-5423">
                      <label className="block text-[10px] text-gray-500 mb-1" data-id="element-5424">
                        Assign Category
                      </label>
                      <select className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs py-1.5" value={rule.Category_ID} onChange={e => {
                  const newRules = [...formData.categoryRules];
                  const catId = e.target.value;
                  const cat = categories.find(c => c.id === catId);
                  newRules[index].Category_ID = catId;
                  newRules[index].Category_Name = cat ? cat.Name : '';
                  setFormData({
                    ...formData,
                    categoryRules: newRules
                  });
                }} data-id="element-5425">
                        <option value="" data-id="element-5426">Select category...</option>
                        {categories.map(cat => <option key={cat.id} value={cat.id} data-id="element-5427">
                            {cat.Name}
                          </option>)}
                      </select>
                    </div>
                  </div>)}
              </div> : <div className="text-center py-6 bg-gray-50 border border-gray-200 border-dashed rounded-md" data-id="element-5428">
                <p className="text-sm text-gray-500" data-id="element-5429">
                  No category rules defined.
                </p>
                <button type="button" onClick={() => {
              setFormData(prev => ({
                ...prev,
                categoryRules: [{
                  Form_Field: formFields[0] || '',
                  Condition: 'equals',
                  Value: '',
                  Category_ID: '',
                  Category_Name: ''
                }]
              }));
            }} className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium" data-id="element-5430">
                  Add your first rule
                </button>
              </div>}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200" data-id="element-5431">
          <Link to="/newsletters" className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-5432">
            Cancel
          </Link>
          <button type="submit" disabled={saving} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50" data-id="element-5433">
            {saving && <Loader2 className="h-4 w-4 animate-spin" data-id="element-5434" />}
            Save Newsletter
          </button>
        </div>
      </form>
    </div>;
}